import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Text,
  TrashIcon,
  AddStripIcon,
  HbTooltip,
  Box,
  AlertInfoIcon,
  HStack,
  Button,
} from '@cdl/heartbeat-react';
import { SidebarSection } from 'shared/components/SidebarSection';
import { AutoLabelList } from './AutoLabelList';
import { useAbnormalityByCode } from '../../diagnostic/hooks/useAbnormalityByCode';
import {
  labelsToAddSelector,
  labelsToRemoveSelector,
  codesToAddToAssessSelector,
  codesToRemoveToAssessSelector,
  needToAssessSelector,
  codesToAddAssessedSelector,
  codesToRemoveAssessedSelector,
  hasSuggestionsToAssessSelector,
} from '../selectors/auto-labels.selectors';
import { manualAbnormalitiesSelector } from '../../diagnostic/selectors/user-diagnostic.selectors';
import * as autoLabelsActions from '../actions/auto-labels.actions';
import { saveManualAbnormalities } from '../../diagnostic/thunks/save-manual-abnormalities.thunk';
import { updateAutoLabelsToAssess } from '../thunks/update-auto-labels-to-assess.thunk';

export const AutoLabelsEditor: React.FC = () => {
  const dispatch = useDispatch();
  const userAbnormalities = useSelector(manualAbnormalitiesSelector);
  const needToAssess = useSelector(needToAssessSelector);
  const hasSuggestionsToAssess = useSelector(hasSuggestionsToAssessSelector);
  const codesToAddToAssess = useSelector(codesToAddToAssessSelector);
  const codesToRemoveToAssess = useSelector(codesToRemoveToAssessSelector);
  const codesToAddAssessed = useSelector(codesToAddAssessedSelector);
  const codesToRemoveAssessed = useSelector(codesToRemoveAssessedSelector);
  const abnormalityByCode = useAbnormalityByCode();
  const labelsToAdd = useSelector(labelsToAddSelector);
  const labelsToRemove = useSelector(labelsToRemoveSelector);

  const displayedCodeToAdd = useMemo(
    () => labelsToAdd.map((label) => label.code),
    [labelsToAdd],
  );

  const displayedCodeToRemove = useMemo(
    () => labelsToRemove.map((label) => label.code),
    [labelsToRemove],
  );

  const abnormalitiesToAddToAssess = useMemo(
    () => codesToAddToAssess.map((code) => abnormalityByCode[code]),
    [codesToAddToAssess, abnormalityByCode],
  );

  const abnormalitiesToRemoveToAssess = useMemo(
    () => codesToRemoveToAssess.map((code) => abnormalityByCode[code]),
    [codesToRemoveToAssess, abnormalityByCode],
  );

  const handleApplyChanges = useCallback(() => {
    const abnormalitiesToAddAssessed = codesToAddAssessed.map(
      (code) => abnormalityByCode[code],
    );

    const newAbnormalities = userAbnormalities
      .filter(({ code }) => !codesToRemoveAssessed.includes(code))
      .concat(abnormalitiesToAddAssessed);
    dispatch(saveManualAbnormalities(newAbnormalities));

    dispatch(
      autoLabelsActions.setCodeSeen({
        codeToAddSeen: displayedCodeToAdd,
        codeToRemoveSeen: displayedCodeToRemove,
        meta: { history: false },
      }),
    );

    dispatch(
      autoLabelsActions.addAssessmentLogs({
        labelsToAdd,
        labelsToRemove,
        abnormalities: userAbnormalities,
        meta: { history: false },
      }),
    );

    dispatch(
      autoLabelsActions.setAssessmentStatus({
        status: 'done',
        meta: { history: false },
      }),
    );
  }, [
    abnormalityByCode,
    displayedCodeToAdd,
    displayedCodeToRemove,
    codesToAddAssessed,
    codesToRemoveAssessed,
    dispatch,
    labelsToAdd,
    labelsToRemove,
    userAbnormalities,
  ]);

  useEffect(() => {
    if (needToAssess) {
      dispatch(updateAutoLabelsToAssess());
      if (!hasSuggestionsToAssess) {
        dispatch(
          autoLabelsActions.setAssessmentStatus({
            status: 'none',
            meta: { history: false },
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, needToAssess, codesToAddToAssess, codesToRemoveToAssess]);

  if (
    !needToAssess ||
    (abnormalitiesToAddToAssess?.length === 0 &&
      abnormalitiesToRemoveToAssess?.length === 0)
  ) {
    return null;
  }

  return (
    <SidebarSection data-testid='auto-labels-editor'>
      <HStack>
        <Text textStyle='section-subtitle'>Automatic labels suggestion</Text>
        <HbTooltip
          hasArrow
          label={
            <>
              <Text>Suggestions based on metrics</Text>
            </>
          }
        >
          <Box display='flex' justifyContent='center' alignItems='center'>
            <AlertInfoIcon />
          </Box>
        </HbTooltip>
      </HStack>

      {abnormalitiesToAddToAssess?.length > 0 && (
        <>
          <Text textStyle='category-subtitle'>
            <AddStripIcon size='s' /> Which label do you want to add?
          </Text>
          <AutoLabelList
            labelColor='green'
            abnormalities={abnormalitiesToAddToAssess}
          />
        </>
      )}

      {abnormalitiesToRemoveToAssess?.length > 0 && (
        <>
          <Text textStyle='category-subtitle'>
            <TrashIcon size='xs' /> Which label do you want to remove?
          </Text>
          <AutoLabelList
            labelColor='red'
            abnormalities={abnormalitiesToRemoveToAssess}
          />
        </>
      )}
      <HStack justifyContent='end'>
        <Button
          size='xs'
          data-testid='apply-suggestion-btn'
          onClick={handleApplyChanges}
        >
          Apply changes
        </Button>
      </HStack>
    </SidebarSection>
  );
};
